// 📄 ReportDetails.js
import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import {
    FormGroup,
    Label,
    Input,
    Button,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Row,
    Col,
    Spinner,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter
} from 'reactstrap';

import ReactJson from 'react-json-view';

import reportService from 'services/report.service';
import membersService from 'services/staff.service';
import authService from 'services/auth.service';
import settingService from 'services/setting.service';

const jwt = require('jsonwebtoken');

function ReportDetails(props) {
    const data = props.data || {};
    const onBack = props.onBack || (() => { });

    const [id, setId] = useState(data.id || '');
    const [reportBy, setReportBy] = useState(data.reportBy || '');
    const [childIndex, setChildIndex] = useState(data.childIndex || '');
    const [toolName, setToolName] = useState(data.toolName || '');
    const [errorMessage, setErrorMessage] = useState(data.errorMessage || '');
    const [stackTrace, setStackTrace] = useState(data.stackTrace || '');
    const [status, setStatus] = useState(data.status || 'Pending');
    const [adminResponse, setAdminResponse] = useState(data.adminResponse || '');
    const [attachment, setAttachment] = useState(data.attachment || '');
    const [errorData, setErrorData] = useState(data.errorData || '');
    const [errorDataShort, setErrorDataShort] = useState(data.errorDataShort || '');
    const [members, setMembers] = useState([]);
    const [selectedMember, setSelectedMember] = useState(data.handledBy || '');;
    const [note, setNote] = useState('');
    const [imageURLs, setImageURLs] = useState([]);
    const [aiLoading, setAiLoading] = useState(false);
    const [rewriteLoading, setRewriteLoading] = useState(false);
    const [rewriteNote, setRewriteNote] = useState('');
    const [selectedAIModel, setSelectedAIModel] = useState('');
    const [availableModels, setAvailableModels] = useState([]);
    const [showAIModal, setShowAIModal] = useState(false);
    const [modalResponse, setModalResponse] = useState('');

    useEffect(() => {
        fetchReportDetails();
        fetchMembers();
        fetchAIModels();
    }, []);

    /** 🤖 Tải danh sách AI models (custom + built-in) */
    async function fetchAIModels() {
        try {
            const BUILTIN_MODELS = [
                { value: 'gpt-5.4-mini', label: 'GPT-5.4 Mini' },
                { value: 'gpt-5.4-nano', label: 'GPT-5.4 Nano' },
                { value: 'gpt-5.4', label: 'GPT-5.4' },
                { value: 'gpt-4o', label: 'GPT-4o' },
                { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
            ];

            const res = await settingService.getAll();
            let customModels = [];
            let defaultModel = '';
            if (res.data?.success) {
                const settings = res.data.settings;
                const cmSetting = settings.find(s => s.key === 'openaiCustomModels');
                const modelSetting = settings.find(s => s.key === 'openaiModel');
                if (cmSetting?.value && Array.isArray(cmSetting.value)) {
                    customModels = cmSetting.value.map(m => ({
                        value: m.modelId,
                        label: `⭐ ${m.label}`,
                        rawLabel: m.label.toLowerCase(),
                        isCustom: true
                    }));
                }
                if (modelSetting?.value) {
                    defaultModel = modelSetting.value;
                }
            }

            const allModels = [
                { value: '', label: `Mặc định (${defaultModel || 'gpt-5.4-mini'})` },
                ...customModels,
                ...BUILTIN_MODELS
            ];
            setAvailableModels(allModels);
        } catch (err) {
            console.error('Lỗi lấy AI models:', err);
        }
    }

    /** 📥 Tải chi tiết báo lỗi */
    async function fetchReportDetails() {
        const result = await reportService.getReportDetails(id);
        const report = result.data;
        setAdminResponse(report.adminResponse);
        setAttachment(report.attachment);
        setErrorData(report.errorData);
        setErrorDataShort(report.errorDataShort);
        setNote(report.note);

        const urls = await Promise.all(
            report.imageAttachments.map(async (filename) => {
                const url = await reportService.downloadImage(filename.split('/').pop());
                return url;
            })
        );

        setImageURLs(urls);
    }

    /** 📥 Tải tệp đính kèm */
    function downloadAttachment() {
        if (!attachment) {
            Swal.fire('Thông báo', 'Không có tệp đính kèm.', 'info');
            return;
        }
        reportService.downloadAttachment(attachment)
            .catch(() => {
                Swal.fire('Lỗi!', 'Không thể tải tệp đính kèm.', 'error');
            });
    }

    /** 📥 Lấy danh sách thành viên */
    function fetchMembers() {
        reportService.getAllUsers()
            .then(response => {
                setMembers(response);

                if (selectedMember) {
                    // Nếu đã chọn thành viên trước đó, chọn lại thành viên đó
                    var selected = response.find(member => member.username === selectedMember.username);
                    setSelectedMember(selected);
                } else {
                    // Nếu chưa chọn thành viên, chọn thành viên hiện tại
                    const user = authService.getCurrentUserData();
                    const decoded = jwt.decode(user.token, { complete: true });
                    var selected = response.find(member => member.username === decoded.payload.username);
                    setSelectedMember(selected);
                }
            })
            .catch(error => {
                console.error("Lỗi khi lấy danh sách thành viên:", error);
            });
    }

    /** 📤 Gửi phản hồi */
    function updateReport() {
        const data = {
            handlerByUsername: selectedMember.username,
            status: status,
            adminResponse: adminResponse
        };

        reportService.updateUserReport(id, data)
            .then(() => {
                Swal.fire('Thành công!', 'Cập nhập báo lỗi thành công.', 'success');
                onBack();
            })
            .catch(() => {
                Swal.fire('Lỗi!', 'Không thể cập nhập báo lỗi.', 'error');
            });
    }

    // Xóa báo lỗi
    function deleteReport() {
        Swal.fire({
            title: 'Xác nhận xóa báo lỗi?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy',
        }).then((result) => {
            if (result.isConfirmed) {
                reportService.deleteReport(id).then(() => {
                    Swal.fire('Thành công!', 'Xóa báo lỗi thành công', 'success');
                    onBack();
                }).catch((error) => {
                    Swal.fire('Lỗi!', `Không thể xóa báo lỗi <br><br> ${error}`, 'error');
                });
            }
        });
    }

    /** 🤖 Mở modal AI — auto-select model theo toolName */
    function openAIModal() {
        setModalResponse(adminResponse || '');
        setRewriteNote('');

        // Auto-select: match toolName với custom model label
        const tool = (toolName || '').toLowerCase().replace(/\s+/g, '');
        if (tool && availableModels.length > 0) {
            const matched = availableModels.find(m => {
                if (!m.isCustom || !m.rawLabel) return false;
                const raw = m.rawLabel.replace(/\s+/g, '');
                return raw.includes(tool) || tool.includes(raw);
            });
            setSelectedAIModel(matched ? matched.value : '');
        } else {
            setSelectedAIModel('');
        }

        setShowAIModal(true);
    }

    /** 🤖 Xử lý với AI (trong modal) */
    async function handleAIAnalyze() {
        setAiLoading(true);
        try {
            const modelToUse = selectedAIModel || null;
            const result = await reportService.aiAnalyze(id, modelToUse);
            if (result.success) {
                setModalResponse(result.response);
            } else {
                Swal.fire('Lỗi', result.message || 'Không thể phân tích', 'error');
            }
        } catch (error) {
            const msg = error.response?.data?.message || error.message || 'Lỗi không xác định';
            Swal.fire('Lỗi AI', msg, 'error');
        } finally {
            setAiLoading(false);
        }
    }

    /** ✨ Viết lại với AI từ ghi chú ngắn (trong modal) */
    async function handleAIRewrite() {
        if (!rewriteNote.trim()) {
            Swal.fire('Thông báo', 'Vui lòng nhập ghi chú hướng dẫn trước', 'warning');
            return;
        }
        setRewriteLoading(true);
        try {
            const modelToUse = selectedAIModel || null;
            const result = await reportService.aiRewrite(id, rewriteNote, modelToUse);
            if (result.success) {
                setModalResponse(result.response);
                setRewriteNote('');
            } else {
                Swal.fire('Lỗi', result.message || 'Không thể viết lại', 'error');
            }
        } catch (error) {
            const msg = error.response?.data?.message || error.message || 'Lỗi không xác định';
            Swal.fire('Lỗi AI', msg, 'error');
        } finally {
            setRewriteLoading(false);
        }
    }

    /** ✅ Xác nhận phản hồi AI → đổ về adminResponse + Resolved */
    function handleAcceptAIResponse() {
        setAdminResponse(modalResponse);
        setStatus('Resolved');
        setShowAIModal(false);
        setRewriteNote('');
    }

    // Hàm giải mã Base64 an toàn với UTF-8
    function decodeBase64(value) {
        try {
            if (typeof value === 'string' && /^[A-Za-z0-9+/=]+$/.test(value)) {
                const decoded = atob(value);
                const utf8Decoded = decodeURIComponent(escape(decoded)); // Chuyển sang UTF-8
                // Kiểm tra nếu kết quả có thể parse thành JSON
                try {
                    return JSON.parse(utf8Decoded);
                } catch {
                    return utf8Decoded;
                }
            }
        } catch (e) {
        }

        return value; // Nếu thất bại, trả về giá trị gốc
    }

    // Hàm đệ quy để giải mã toàn bộ object
    function decodeBase64Recursive(data) {
        if (Array.isArray(data)) {
            return data.map(decodeBase64Recursive);
        } else if (typeof data === 'object' && data !== null) {
            return Object.fromEntries(
                Object.entries(data).map(([key, value]) => [key, decodeBase64Recursive(decodeBase64(value))])
            );
        }
        return decodeBase64(data);
    }

    return (
        <div className="content">
            <Row>
                {/* Chi tiết báo lỗi (9 cột) */}
                <Col lg="9" md="8">
                    <Card style={{ backgroundColor: '#fff', borderRadius: '20px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden', margin: '20px auto' }}>
                        <CardHeader style={{ background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)', padding: '18px 24px', border: 'none', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <i className="fas fa-exclamation-triangle" style={{ fontSize: '32px', color: '#fff', opacity: 0.9 }}></i>
                            <div style={{ lineHeight: '1.3' }}>
                                <Label style={{ fontSize: '18px', color: '#fff', fontWeight: '700', marginBottom: '0', display: 'block' }}>Chi tiết báo lỗi</Label>
                                <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>Báo cáo từ người dùng</span>
                            </div>
                        </CardHeader>
                        <CardBody style={{ padding: '20px 24px' }}>
                            <Row>
                                <Col md={4}>
                                    <FormGroup>
                                        <Label>Username</Label>
                                        <Input value={`${reportBy.fullname} (${reportBy.username})`} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <Label>Child Index</Label>
                                        <Input value={reportBy.childIndex} disabled />
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <Label>Tool Name</Label>
                                        <Input value={toolName} disabled />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <FormGroup style={{ marginTop: '5px' }}>
                                <Label>Stack Trace</Label>
                                <Input value={stackTrace} disabled />
                            </FormGroup>

                            <FormGroup style={{ marginTop: '15px' }}>
                                <Label>Data Short</Label>
                                <Input type="textarea" style={{ minHeight: '250px' }} value={errorDataShort} />
                            </FormGroup>

                            <FormGroup style={{ marginTop: '15px' }}>
                                <Label>Data</Label>
                                <div style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #ccc',
                                    borderRadius: '5px',
                                    padding: '10px',
                                    minHeight: '400px',
                                    overflow: 'auto'
                                }}>
                                    {(() => {
                                        try {
                                            // Parse JSON
                                            const parsedData = typeof errorData === 'string' ? JSON.parse(errorData) : errorData;
                                            // Giải mã Base64
                                            const decodedData = decodeBase64Recursive(parsedData);

                                            return (
                                                <ReactJson
                                                    src={decodedData}
                                                    theme="rjv-default"
                                                    collapsed={1} // Collapse tất cả cấp độ
                                                    enableClipboard={false}
                                                    displayDataTypes={false}
                                                    style={{
                                                        borderRadius: '5px',
                                                        fontSize: '14px'
                                                    }}
                                                />
                                            );
                                        } catch (e) {
                                            console.error('Lỗi parse JSON:', e);
                                            return (
                                                <pre style={{
                                                    whiteSpace: 'pre-wrap',
                                                    wordWrap: 'break-word',
                                                    fontSize: '14px',
                                                    fontFamily: 'monospace',
                                                    color: '#d9534f'
                                                }}>
                                                    {errorData || 'Không có dữ liệu'}
                                                </pre>
                                            );
                                        }
                                    })()}
                                </div>
                            </FormGroup>


                            <>
                                {
                                    attachment ?
                                        <FormGroup style={{ marginTop: '15px' }}>
                                            <Label>Attachment</Label>
                                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '-15px' }}>
                                                <Input value={attachment || 'Không có tệp đính kèm'} readOnly />
                                                <Button color="success" style={{ marginLeft: '10px', width: '150px', borderRadius: '20px' }} onClick={downloadAttachment}>
                                                    <i className="fas fa-download" style={{ marginRight: '5px' }}></i> Tải tệp
                                                </Button>
                                            </div>
                                        </FormGroup>
                                        :
                                        null
                                }
                            </>

                            <FormGroup style={{ marginTop: '15px' }}>
                                <Label>Images</Label>
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '10px'
                                }}>
                                    {imageURLs.length > 0 ? (
                                        imageURLs.map((url, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    width: '100px',
                                                    height: '100px',
                                                    overflow: 'hidden',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    border: '1px solid #ccc',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    background: '#f9f9f9'
                                                }}
                                                onClick={() => {
                                                    Swal.fire({
                                                        imageUrl: url,
                                                        imageAlt: `Attachment ${index + 1}`,
                                                        showCloseButton: true,
                                                        showConfirmButton: false,
                                                        width: '80%',
                                                        background: '#fff'
                                                    });
                                                }}
                                            >
                                                <img
                                                    src={url}
                                                    alt={`Attachment ${index + 1}`}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                            </div>
                                        ))
                                    ) : (
                                        <p style={{ fontStyle: 'italic', color: '#888' }}>Không có ảnh đính kèm</p>
                                    )}
                                </div>
                            </FormGroup>
                        </CardBody>
                    </Card>
                </Col>

                {/* Thao tác (3 cột) */}
                <Col lg="3" md="4">
                    {/* Bảng Nhận Việc */}
                    <Card style={{ backgroundColor: '#fff', borderRadius: '20px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden', margin: '20px auto' }}>
                        <CardHeader style={{ background: 'linear-gradient(135deg, #198754 0%, #0f5132 100%)', padding: '18px 24px', border: 'none', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <i className="fas fa-tasks" style={{ fontSize: '32px', color: '#fff', opacity: 0.9 }}></i>
                            <div style={{ lineHeight: '1.3' }}>
                                <Label style={{ fontSize: '18px', color: '#fff', fontWeight: '700', marginBottom: '0', display: 'block' }}>Xử lý</Label>
                                <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>Phân công & phản hồi</span>
                            </div>
                        </CardHeader>
                        <CardBody style={{ padding: '20px 24px' }}>
                            <FormGroup>
                                <Label>Người xử lý</Label>
                                <Input
                                    type="select"
                                    value={selectedMember?.username || ''} // Hiển thị username nếu đã chọn
                                    onChange={(e) => {
                                        const selected = members.find(member => member.username === e.target.value);
                                        setSelectedMember(selected || null);
                                    }}
                                >
                                    {members.map(member => (
                                        <option key={member.id} value={member.username}>
                                            {member.fullname} ({member.username})
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>

                            <FormGroup style={{ marginTop: '15px' }}>
                                <Label>Trạng thái</Label>
                                <Input type="select" value={status} onChange={(e) => setStatus(e.target.value)}>
                                    <option value="Pending">Pending</option>
                                    <option value="InProgress">InProgress</option>
                                    <option value="Resolved">Resolved</option>
                                    <option value="Ignored">Ignored</option>
                                </Input>
                            </FormGroup>
                            <FormGroup style={{ marginTop: '15px' }}>
                                <Label>Ghi chú từ khách hàng</Label>
                                <Input type="textarea" value={note} style={{ minHeight: '150px' }} disabled />
                            </FormGroup>

                            <FormGroup style={{ marginTop: '15px' }}>
                                <Label>Phản hồi cho khách hàng</Label>
                                <Button
                                    color="info"
                                    onClick={openAIModal}
                                    style={{
                                        width: '100%',
                                        borderRadius: '12px',
                                        marginBottom: '10px',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        background: 'linear-gradient(135deg, #8e44ad 0%, #6c3483 100%)',
                                        border: 'none',
                                        padding: '8px 16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <i className="fas fa-robot"></i> Xử lý với AI
                                </Button>
                                <Input type="textarea" value={adminResponse} style={{ minHeight: '200px' }} onChange={(e) => setAdminResponse(e.target.value)} />
                            </FormGroup>
                        </CardBody>
                        <CardFooter style={{ textAlign: 'left', marginTop: '-10px', marginBottom: '10px' }}>
                            <Button color="success" style={{ width: '100%', borderRadius: '20px' }} onClick={updateReport}><i className="fas fa-sync-alt" style={{ marginRight: '5px' }}></i> Cập nhập</Button>
                            <Button color="danger" style={{ width: '100%', borderRadius: '20px', marginTop: '10px' }} onClick={deleteReport}><i className="fas fa-times" style={{ marginRight: '5px' }}></i> Xóa báo lỗi</Button>
                        </CardFooter>
                    </Card>
                </Col>
            </Row>
            {/* ═══ AI Modal Dialog ═══ */}
            <Modal isOpen={showAIModal} toggle={() => setShowAIModal(false)} size="lg" centered
                style={{ maxWidth: '700px' }}
            >
                <ModalHeader toggle={() => setShowAIModal(false)} style={{
                    background: 'linear-gradient(135deg, #8e44ad 0%, #6c3483 100%)',
                    color: '#fff',
                    borderBottom: 'none',
                    borderRadius: '0'
                }}>
                    <i className="fas fa-robot" style={{ marginRight: '8px' }}></i>
                    Xử lý với AI
                </ModalHeader>
                <ModalBody style={{ padding: '24px' }}>
                    {/* Chọn model AI */}
                    <FormGroup>
                        <Label style={{ fontSize: '13px', fontWeight: '600', color: '#2c3e50' }}>
                            <i className="fas fa-brain" style={{ marginRight: '6px', color: '#8e44ad' }}></i>
                            Mô hình AI
                        </Label>
                        {availableModels.length > 0 && (
                            <Input
                                type="select"
                                value={selectedAIModel}
                                onChange={(e) => setSelectedAIModel(e.target.value)}
                                style={{
                                    borderRadius: '10px',
                                    fontSize: '13px',
                                    border: '1.5px solid #e9ecef',
                                    cursor: 'pointer'
                                }}
                            >
                                {availableModels.map((m, idx) => (
                                    <option key={idx} value={m.value}>{m.label}</option>
                                ))}
                            </Input>
                        )}
                    </FormGroup>

                    {/* Nút phân tích */}
                    <Button
                        color="info"
                        disabled={aiLoading}
                        onClick={handleAIAnalyze}
                        style={{
                            width: '100%',
                            borderRadius: '10px',
                            marginBottom: '14px',
                            fontSize: '14px',
                            fontWeight: '600',
                            background: aiLoading ? '#ccc' : 'linear-gradient(135deg, #8e44ad 0%, #6c3483 100%)',
                            border: 'none',
                            padding: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                    >
                        {aiLoading ? (
                            <><Spinner size="sm" /> Đang phân tích...</>
                        ) : (
                            <><i className="fas fa-robot"></i> Phân tích lỗi</>
                        )}
                    </Button>

                    {/* Kết quả AI */}
                    <FormGroup>
                        <Label style={{ fontSize: '13px', fontWeight: '600', color: '#2c3e50' }}>
                            <i className="fas fa-comment-dots" style={{ marginRight: '6px', color: '#27ae60' }}></i>
                            Phản hồi AI
                        </Label>
                        <Input
                            type="textarea"
                            value={modalResponse}
                            onChange={(e) => setModalResponse(e.target.value)}
                            placeholder="Nhấn 'Phân tích lỗi' để AI tạo phản hồi..."
                            style={{
                                minHeight: '200px',
                                borderRadius: '10px',
                                fontSize: '13px',
                                border: '1.5px solid #e9ecef',
                                lineHeight: '1.6'
                            }}
                        />
                    </FormGroup>

                    {/* Ghi chú sửa + viết lại */}
                    <div style={{
                        padding: '14px',
                        borderRadius: '12px',
                        backgroundColor: '#f8f4ff',
                        border: '1px dashed #8e44ad'
                    }}>
                        <Label style={{ fontSize: '12px', fontWeight: '600', color: '#6c3483', marginBottom: '8px' }}>
                            <i className="fas fa-magic" style={{ marginRight: '4px' }}></i>
                            Ghi chú sửa (AI sẽ viết lại chuyên nghiệp)
                        </Label>
                        <Input
                            type="textarea"
                            value={rewriteNote}
                            onChange={(e) => setRewriteNote(e.target.value)}
                            placeholder="VD: Hướng dẫn vào Template → Speech → đổi giọng yannew"
                            style={{
                                minHeight: '70px',
                                borderRadius: '10px',
                                fontSize: '13px',
                                border: '1.5px solid #e9ecef',
                                marginBottom: '8px',
                                padding: '10px 14px'
                            }}
                        />
                        <Button
                            color="warning"
                            disabled={rewriteLoading || !rewriteNote.trim()}
                            onClick={handleAIRewrite}
                            style={{
                                width: '100%',
                                borderRadius: '10px',
                                fontSize: '13px',
                                fontWeight: '600',
                                background: rewriteLoading || !rewriteNote.trim() ? '#ccc' : 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)',
                                border: 'none',
                                color: '#fff',
                                padding: '8px 14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px'
                            }}
                        >
                            {rewriteLoading ? (
                                <><Spinner size="sm" /> Đang viết lại...</>
                            ) : (
                                <><i className="fas fa-magic"></i> Viết lại với AI</>
                            )}
                        </Button>
                    </div>
                </ModalBody>
                <ModalFooter style={{ borderTop: '1px solid #f0f0f0', padding: '14px 24px' }}>
                    <Button
                        color="secondary"
                        onClick={() => setShowAIModal(false)}
                        style={{ borderRadius: '10px', fontSize: '13px' }}
                    >
                        Hủy
                    </Button>
                    <Button
                        color="success"
                        disabled={!modalResponse.trim()}
                        onClick={handleAcceptAIResponse}
                        style={{
                            borderRadius: '10px',
                            fontSize: '13px',
                            fontWeight: '600',
                            background: modalResponse.trim() ? 'linear-gradient(135deg, #27ae60 0%, #1e8449 100%)' : '#ccc',
                            border: 'none',
                            padding: '8px 20px'
                        }}
                    >
                        <i className="fas fa-check" style={{ marginRight: '6px' }}></i>
                        Xác nhận phản hồi
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}



export default ReportDetails;
